import React, {useEffect, useState} from 'react';
import {withTranslation} from 'react-i18next';
import {useSelector} from 'react-redux';
import {useNavigate} from 'react-router-dom';
import swal from 'sweetalert';
import AddButton from '../../components/AddButton';
import DataTable from '../../components/DataTable';
import DeleteButton from '../../components/DeleteButton';
import EditButton from '../../components/EditButton';
import Pagination from '../../components/Pagination';
import Select from '../../components/Select';
import {action, deleteItem, getTableWithFilter} from '../../services/service';
import SettingButton from "../../components/SettingButton";
import SearchInput from "../../components/search/SearchInput";
import {Context} from "../../context/SearchContext";
import {useContext} from "react";
import {toast} from "react-toastify";
import {deleteFilling} from "../../services/fillingService";
import TempDataTable from "../../components/TemDataTable";
import AccessoriesSideLayout from "./AccessoriesSideLayout";
import TemDataTableInline from "../../components/TemDataTableInline";
import CopyButton from "../../components/CopyButton";
import AccessoriesParameter from "./AccessoriesParameter";


const headerTable = [{
    name: "id", sort: false
}, {
    name: "name", sort: true
}, {
    name: "shortName", sort: true
}, {
    name: "openingType", sort: true
}, {
    name: "openingSide", sort: false
}, {
    name: "accessoriesType", sort: true
}, {
    name: "handleSide", sort: false
}, {
    name: "maxHalfPerimeter", sort: false
}, {
    name: "maxHeight", sort: false
}, {
    name: "maxWidth", sort: false
}, {
    name: "maxSash", sort: false
}, <span className="sr-only">Edit</span>,];

const bodyTable = [
    "id",
    'name',
    'shortName',
    'openingType',
    'openingSide',
    "accessoriesType",
    "handleSide",
    "maxHalfPerimeter",
    "maxHeight",
    "maxWidth",
    "maxSash",
    "actions"
];

function AccessoriesLayout({t}) {
    const navigate = useNavigate();
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [itemOffset, setItemOffset] = useState(0);
    const [forcePage, setForcePage] = useState(0);
    const [data, setData] = useState({
        data: [],
        recordsTotal: 0,
        pageCount: 1,
    });
    const [isLoading, setIsLoading] = useState(false);
    const {token} = useSelector((state) => state.user);
    const [sortColumn, setSortColumn] = useState("id");
    const [sortDir, setSortDir] = useState("DESC");
    const [clear, setClear] = useState(false);
    const [selectedId, setSelectedId] = useState(null);
    const ctx = useContext(Context);

    const handlePageClick = (event) => {
        setForcePage(event.selected);
        const newOffset =
            (event.selected * parseInt(itemsPerPage)) % data.recordsTotal;
        setItemOffset(newOffset);
    };

    const deleteHandler = async (id) => {
        const responseReceived = await swal({
            title: t(`action.delete_item.title`),
            text: t(`action.delete_item.text`),
            icon: "warning",
            buttons: {
                cancel: true,
                confirm: true,
            },
        });
        if (responseReceived) {
            setIsLoading(true);
            const response = await deleteFilling(
                `${process.env.REACT_APP_HOST_BASE_URL}/v1/accessories/${id}`,
                token
            );
            if (response.success) {
                setIsLoading(false);
                toast.success(t('action.delete_success'), {
                    position: "bottom-right",
                    autoClose: 3000,
                    closeOnClick: true,
                    className: "add-success"
                });
            } else {
                setIsLoading(false);
                if (response.status === 420) {
                    toast.warn(response.body.error, {
                        position: "bottom-right",
                        autoClose: 10000,
                        closeOnClick: true,
                        className: "product-background",
                        pauseOnHover: true,
                    });
                } else {
                    navigate("/error", {
                        state: {
                            status: response.status,
                            message: response.body ? response.body.error : "Error",
                        },
                    });
                }
            }
        } else {
            return;
        }
    };

    const fetchData = async () => {
        const response = await getTableWithFilter(
            `${process.env.REACT_APP_HOST_BASE_URL}/v1/accessories/table`,
            token,
            {
                draw: 1,
                start: itemOffset,
                length: itemsPerPage,
                recordsTotal: 0,
                columns: [],
                order: [{column: sortColumn, dir: sortDir}],
                search: {value: ctx.state, regex: true},
                filter: {},
            }
        );
        if (response.success) {
            setClear(false);
            let reviewData = response.data.data.map((item, index) => {
                return {
                    id: item.id,
                    name: item.name,
                    shortName: item.shortName,
                    openingType: t(item.openingType),
                    openingSide: t(item.openingSide || 'INWARD'),
                    accessoriesType: t(item.accessoriesType),
                    handleSide: item.handleSide,
                    maxHalfPerimeter: item.maxHalfPerimeter,
                    maxHeight: item.maxHeight,
                    maxWidth: item.maxWidth,
                    maxSash: item.maxSash,
                    actions: (
                        <>
                            <SettingButton onclick={(e) => onclickSetting(item)}/>
                            <CopyButton onclick={() => onCopyRow(item.id)}/>
                            <EditButton onclick={() => navigate(`/furniture/update/${item.id}`)}/>
                            <DeleteButton onclick={() => deleteHandler(item.id)}/>
                        </>
                    ),
                };
            });
            setData({
                data: reviewData,
                recordsTotal: response.data.recordsFiltered,
                pageCount: Math.ceil(
                    response.data.recordsFiltered / parseInt(itemsPerPage)
                ),
            });
        } else {
            navigate("/error", {
                state: {
                    status: response.status,
                    message: response.message ? response.message : null,
                },
            });
        }
    };

    useEffect(() => {
        if (clear) {
            fetchData();
        }
    }, [clear]);

    useEffect(() => {
        fetchData();
    }, [itemOffset, isLoading, itemsPerPage, data.recordsTotal, ctx.state]);

    const onclickSetting = (item) => {
        if (item?.id > 0) {
            navigate(`/furniture/main/specification/${item.id}`, {
                state: {item: item},
            });
        }
    };

    const sortFunction = (value) => {
        setSortColumn(value.field ? value.field : "id");
        setSortDir(value.sort === true ? 'ASC' : 'DESC');
        setClear(true);
    }
    const onCopyRow = async (id) => {
        const responseReceived = await swal({
            title: t(`action.copy_item.title`),
            text: t(`action.copy_item.text`),
            icon: "warning",
            buttons: {
                cancel: true,
                confirm: true,
            },
        });
        if (responseReceived) {
            setIsLoading(true);
            let url = `${process.env.REACT_APP_HOST_BASE_URL}/v1/accessories/copy/${id}`;
            let res = await action(url, "POST", token);
            if (res.success) {
                fetchData();
                setIsLoading(false);

            } else {
                navigate("/error", {
                    state: {
                        status: res.status,
                        message: res.message ? res.message : null,
                    },
                });
            }
        }
    };

    return (
        <>
            <div className="px-4 py-4 sm:px-6 lg:px-8">
                <div className="mb-2 sm:flex sm:items-center">
                    <div className="sm:flex-auto">
                        <h1 className="text-xl font-semibold text-gray-900">{t(`list_accessories`)}</h1>
                    </div>
                    <AddButton title={t('action.add')} url={"add"}/>
                </div>
                <div>
                    <div className="flex justify-between items-center">
                        <Select
                            setItemsPerPage={setItemsPerPage}
                            itemsPerPage={itemsPerPage}
                        />
                        <SearchInput/>
                    </div>
                    <TemDataTableInline
                        data={data.data}
                        headerTable={headerTable}
                        bodyContent={bodyTable}
                        rowHandler={(id) => {
                            if (id !== selectedId) {
                                setSelectedId(id);
                            }
                        }}
                        sortFunction={sortFunction}
                    />
                    <Pagination
                        handlePageClick={handlePageClick}
                        pageCount={data.pageCount}
                        forcePage={forcePage}
                    />
                </div>
            </div>
            {
                selectedId ?
                    <div className="px-4 py-4 sm:px-6 lg:px-8">
                        <div className="grid grid-cols-6 mt-4 gap-x-5">
                            <div className="col-span-6 sm:col-span-3">
                                <AccessoriesSideLayout selectedId={selectedId}/>
                            </div>
                            <div className="col-span-6 sm:col-span-3">
                                <AccessoriesParameter selectedId={selectedId}/>
                            </div>
                        </div>
                    </div>: ''
            }

        </>
    )
}

export default withTranslation()(AccessoriesLayout)
